import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * RepriceDiamondModal - Inline pricing adjustment modal
 * Updates diamond pricing without leaving dashboard
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import BaseModal from '../shared/BaseModal';
import { notify } from '../../utils/notifications';
const FormGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;
const FormGroup = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'auto'};
`;
const Label = styled.label `
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
`;
const Input = styled.input `
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  background: var(--bg-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }
`;
const InfoBox = styled.div `
  background: var(--bg-tertiary);
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color);
`;
const InfoRow = styled.div `
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }
`;
const Button = styled.button `
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.primary ? '#8b5cf6' : 'var(--border-color)'};
  background: ${props => props.primary ? '#8b5cf6' : 'var(--bg-secondary)'};
  color: ${props => props.primary ? 'white' : 'var(--text-primary)'};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const CalculationBox = styled.div `
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05));
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 6px;
  padding: 1rem;
  margin-top: 1rem;
`;
const CalcRow = styled.div `
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  color: var(--text-secondary);

  &:last-child {
    border-top: 2px solid rgba(139, 92, 246, 0.3);
    margin-top: 0.5rem;
    padding-top: 0.75rem;
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-primary);
  }
`;
export const RepriceDiamondModal = ({ isOpen, onClose, diamond, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [newPricePerCarat, setNewPricePerCarat] = useState(0);
    const [reason, setReason] = useState('');
    useEffect(() => {
        if (diamond && isOpen) {
            setNewPricePerCarat(diamond.pricePerCarat);
            setReason('');
        }
    }, [diamond, isOpen]);
    const handleSubmit = async () => {
        if (!diamond || newPricePerCarat <= 0)
            return;
        setLoading(true);
        try {
            // TODO: Call API to update diamond price
            // For now, just show success message
            notify.success('Price Updated', `Diamond repriced to $${newPricePerCarat.toLocaleString()}/ct`);
            onSuccess?.();
            onClose();
        }
        catch (error) {
            console.error('Failed to reprice diamond:', error);
            notify.error('Error', 'Failed to update price');
        }
        finally {
            setLoading(false);
        }
    };
    if (!diamond)
        return null;
    const newTotalPrice = newPricePerCarat * diamond.caratWeight;
    const priceChange = newPricePerCarat - diamond.pricePerCarat;
    const percentChange = ((priceChange / diamond.pricePerCarat) * 100).toFixed(1);
    const margin = diamond.costPerCarat
        ? ((newPricePerCarat - diamond.costPerCarat) / diamond.costPerCarat * 100).toFixed(1)
        : null;
    return (_jsxs(BaseModal, { isOpen: isOpen, onClose: onClose, title: "Reprice Diamond", maxWidth: "600px", footer: _jsxs(_Fragment, { children: [_jsx(Button, { onClick: onClose, children: "Cancel" }), _jsx(Button, { primary: true, onClick: handleSubmit, disabled: loading || newPricePerCarat <= 0 || !reason.trim(), children: loading ? 'Updating...' : 'Update Price' })] }), children: [_jsxs(InfoBox, { children: [_jsxs(InfoRow, { children: [_jsx("span", { children: "Diamond:" }), _jsxs("strong", { children: [diamond.caratWeight, "ct ", diamond.shape, " ", diamond.color, " ", diamond.clarity] })] }), _jsxs(InfoRow, { children: [_jsx("span", { children: "Stock #:" }), _jsx("strong", { children: diamond.stockNumber || 'N/A' })] }), _jsxs(InfoRow, { children: [_jsx("span", { children: "Current Price/Carat:" }), _jsxs("strong", { children: ["$", diamond.pricePerCarat.toLocaleString()] })] }), _jsxs(InfoRow, { children: [_jsx("span", { children: "Current Total:" }), _jsxs("strong", { children: ["$", diamond.totalPrice.toLocaleString()] })] }), diamond.costPerCarat && (_jsxs(InfoRow, { children: [_jsx("span", { children: "Cost/Carat:" }), _jsxs("strong", { children: ["$", diamond.costPerCarat.toLocaleString()] })] }))] }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "New Price Per Carat *" }), _jsx(Input, { type: "number", step: "100", value: newPricePerCarat, onChange: (e) => setNewPricePerCarat(parseFloat(e.target.value) || 0), placeholder: "Enter new price/carat" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "New Total Price" }), _jsx(Input, { type: "text", value: `$${newTotalPrice.toLocaleString()}`, disabled: true, style: { background: 'var(--bg-tertiary)', cursor: 'not-allowed' } })] }), _jsxs(FormGroup, { "$fullWidth": true, children: [_jsx(Label, { children: "Reason for Price Change *" }), _jsx(Input, { type: "text", value: reason, onChange: (e) => setReason(e.target.value), placeholder: "e.g., Market adjustment, slow-moving, competitive pricing..." })] })] }), _jsxs(CalculationBox, { children: [_jsxs(CalcRow, { children: [_jsx("span", { children: "Price Change:" }), _jsxs("span", { style: { color: priceChange >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }, children: [priceChange >= 0 ? '+' : '', "$", priceChange.toLocaleString(), " (", percentChange, "%)"] })] }), margin !== null && (_jsxs(CalcRow, { children: [_jsx("span", { children: "New Margin:" }), _jsxs("span", { style: { color: parseFloat(margin) >= 20 ? '#10b981' : '#f59e0b', fontWeight: 600 }, children: [margin, "%"] })] })), _jsxs(CalcRow, { children: [_jsx("span", { children: "New Total Price:" }), _jsxs("span", { children: ["$", newTotalPrice.toLocaleString()] })] })] })] }));
};
